import type { BootstrapData } from '../api/bootstrap';
import type { ConditionalRule } from '../config';
import { defaultCustomFieldValues } from '../domain/customFieldValues';
import { usablePaymentMethods } from '../domain/payments';
import type { FormValues, ServiceSelection } from '../domain/payload';
import type { Pane } from '../domain/validation';
import type { FormEvent, FormState } from './types';

export function emptyValues(): FormValues {
  return {
    user: { first_name: '', last_name: '', email: '' },
    address: '',
    apt_suite: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    services: [],
    slot: null,
    frequencyId: null,
    discountCode: '',
    appliedDiscountCode: null,
    customFields: {},
    paymentMethod: null,
  };
}

export function initialState(rules: ConditionalRule[] = []): FormState {
  return {
    status: 'booting',
    step: 1,
    bootstrap: null,
    bootError: null,
    rules,
    values: emptyValues(),
    touched: {},
    attempted: { 1: false, 2: false, 3: false },
    apiErrors: {},
    banner: [],
    focusKey: null,
    pendingNext: false,
    location: { status: 'idle', id: null, message: null, key: null },
    estimate: { status: 'idle', data: null, error: null, key: null },
    spots: { status: 'idle', from: null, days: [], error: null },
    cardComplete: false,
    recaptchaToken: null,
    submitPhase: 'idle',
    submitError: null,
    result: null,
  };
}

/** Defaults derived from the tenant config once it has loaded. */
function valuesFromBootstrap(data: BootstrapData, serviceStates: readonly string[] = []): FormValues {
  const values = emptyValues();
  const defaultFrequency = data.frequencies.find((f) => f.default === true) ?? data.frequencies[0];
  values.frequencyId = typeof defaultFrequency?.id === 'number' ? defaultFrequency.id : null;
  values.customFields = defaultCustomFieldValues(data.customFields);
  const usable = usablePaymentMethods(data.settings);
  values.paymentMethod = usable.length === 1 ? usable[0]! : null;
  // One served state — the field is locked to it, so fill it in from boot.
  if (serviceStates.length === 1 && data.settings.country?.states?.[serviceStates[0]!]) values.state = serviceStates[0]!;
  return values;
}

function withoutApiError(state: FormState, ...keys: string[]): FormState {
  if (!keys.some((k) => state.apiErrors[k])) return state;
  const apiErrors = { ...state.apiErrors };
  for (const k of keys) delete apiErrors[k];
  return { ...state, apiErrors };
}

function patchValues(state: FormState, patch: Partial<FormValues>, ...clearedKeys: string[]): FormState {
  return withoutApiError({ ...state, values: { ...state.values, ...patch } }, ...clearedKeys);
}

function updateSelection(state: FormState, update: (s: ServiceSelection) => ServiceSelection): FormState {
  const current = state.values.services[0];
  if (!current) return state;
  return patchValues(state, { services: [update(current), ...state.values.services.slice(1)] }, 'services');
}

export function reduce(state: FormState, event: FormEvent): FormState {
  switch (event.type) {
    case 'BOOT_OK':
      return { ...state, status: 'ready', bootstrap: event.data, bootError: null, values: valuesFromBootstrap(event.data, event.serviceStates ?? []) };
    case 'BOOT_FAIL':
      return { ...state, status: 'failed', bootError: event.message };

    case 'USER_FIELD':
      return patchValues(state, { user: { ...state.values.user, [event.field]: event.value } }, `user.${event.field}`);
    case 'ADDRESS_FIELD': {
      const next = patchValues(state, { [event.field]: event.value }, event.field);
      // any address change invalidates the service-area result
      return event.field === 'phone' ? next : { ...next, location: { ...next.location, status: next.location.status === 'idle' ? 'idle' : 'idle', message: null, key: null } };
    }

    case 'SERVICE_SELECTED': {
      if (event.serviceId === null) return patchValues(state, { services: [] }, 'services');
      const current = state.values.services[0];
      if (current?.serviceId === event.serviceId) return state;
      return patchValues(state, { services: [{ serviceId: event.serviceId, extras: {}, pricingParameters: {} }] }, 'services');
    }
    case 'EXTRA_SET':
      return updateSelection(state, (s) => {
        const extras = { ...s.extras };
        if (event.quantity > 0) extras[event.extraId] = event.quantity;
        else delete extras[event.extraId];
        return { ...s, extras };
      });
    case 'PRICING_PARAM_SET':
      return updateSelection(state, (s) => ({ ...s, pricingParameters: { ...s.pricingParameters, [event.paramId]: event.quantity } }));

    case 'SLOT_SELECTED':
      return patchValues(state, { slot: event.slot }, 'service_date');
    case 'FREQUENCY_SELECTED':
      return patchValues(state, { frequencyId: event.frequencyId }, 'frequency_id');
    case 'DISCOUNT_CODE_CHANGED':
      return patchValues(state, { discountCode: event.code }, 'discount_code');
    case 'DISCOUNT_CODE_APPLIED':
      return patchValues(state, { appliedDiscountCode: state.values.discountCode.trim() || null }, 'discount_code');
    case 'CUSTOM_FIELD_CHANGED':
      return patchValues(state, { customFields: { ...state.values.customFields, [event.fieldId]: event.value } }, `custom_fields.${event.fieldId}`);
    case 'PAYMENT_METHOD_SELECTED':
      return patchValues(state, { paymentMethod: event.method }, 'payment_method', 'card');
    case 'CARD_STATE':
      return withoutApiError({ ...state, cardComplete: event.complete }, event.complete ? 'card' : '');
    case 'RECAPTCHA':
      return withoutApiError({ ...state, recaptchaToken: event.token }, event.token ? 'recaptcha_token' : '');

    case 'FIELD_BLURRED':
      return state.touched[event.key] ? state : { ...state, touched: { ...state.touched, [event.key]: true } };
    case 'FOCUSED':
      return state.focusKey === null ? state : { ...state, focusKey: null };

    case 'NEXT':
      // The controller validates and either dispatches GO_TO_PANE or marks attempted + focus.
      return { ...state, attempted: { ...state.attempted, [state.step]: true } };
    case 'BACK':
      return state.step === 1 ? state : { ...state, step: (state.step - 1) as FormState['step'], focusKey: null };
    case 'GO_TO_PANE':
      return { ...state, step: event.pane, focusKey: event.focusKey ?? null, pendingNext: false };

    case 'LOCATION_CHECKING':
      return { ...state, location: { status: 'checking', id: state.location.id, message: null, key: event.key }, pendingNext: state.pendingNext };
    case 'LOCATION_OK':
      if (state.location.key !== event.key) return state;
      return withoutApiError({ ...state, location: { status: 'ok', id: event.id, message: null, key: event.key } }, 'zip', 'address');
    case 'LOCATION_BLOCKED':
      if (state.location.key !== event.key) return state;
      return { ...state, location: { status: 'blocked', id: null, message: event.message, key: event.key } };
    case 'LOCATION_ERROR':
      if (state.location.key !== event.key) return state;
      return { ...state, location: { status: 'error', id: state.location.id, message: event.message, key: event.key } };

    case 'ESTIMATE_LOADING':
      return { ...state, estimate: { ...state.estimate, status: 'loading', key: event.key, error: null } };
    case 'ESTIMATE_OK':
      if (state.estimate.key !== event.key) return state;
      return { ...state, estimate: { status: 'ready', data: event.data, error: null, key: event.key } };
    case 'ESTIMATE_FAIL': {
      if (state.estimate.key !== event.key) return state;
      let next: FormState = { ...state, estimate: { ...state.estimate, status: 'error', error: event.message } };
      if (event.fieldErrors['discount_code']) {
        // keep the last good price and drop the rejected code so the next estimate succeeds
        next = { ...next, values: { ...next.values, appliedDiscountCode: null }, apiErrors: { ...next.apiErrors, discount_code: event.fieldErrors['discount_code'] } };
      }
      return next;
    }
    case 'ESTIMATE_CLEARED':
      return { ...state, estimate: { status: 'idle', data: null, error: null, key: null } };

    case 'SPOTS_LOADING':
      return { ...state, spots: { ...state.spots, status: 'loading', from: event.from, error: null } };
    case 'SPOTS_OK':
      if (state.spots.from !== event.from) return state;
      return { ...state, spots: { status: 'ready', from: event.from, days: event.days, error: null } };
    case 'SPOTS_FAIL':
      if (state.spots.from !== event.from) return state;
      return { ...state, spots: { ...state.spots, status: 'error', error: event.message } };

    case 'SUBMIT_STARTED':
      return { ...state, status: 'submitting', submitPhase: 'validating', submitError: null, banner: [], attempted: { 1: true, 2: true, 3: true } };
    case 'SUBMIT_PHASE':
      return { ...state, submitPhase: event.phase };
    case 'SUBMIT_OK':
      return { ...state, status: 'succeeded', submitPhase: 'done', result: event.result };
    case 'SUBMIT_FAILED':
      return {
        ...state,
        status: 'ready',
        submitPhase: 'idle',
        apiErrors: { ...state.apiErrors, ...event.fieldErrors },
        banner: event.banner,
        step: event.pane ?? state.step,
        focusKey: event.focusKey,
        submitError: event.banner[0] ?? null,
      };
    case 'DISMISS_BANNER':
      return { ...state, banner: [], submitError: null };
  }
}

export const lowestPane = (a: Pane | null, b: Pane | null): Pane | null => (a === null ? b : b === null ? a : (Math.min(a, b) as Pane));
